import { supabase } from './supabase';
import { slugify } from './menuManager';
import { seedDefaultOptions } from './defaultOptions';
import { setTenantFeature } from './featuresService';
import {
  type SignupForm,
  currencyForCountry,
  siteUrlFor,
  sitePathFor,
} from './signup';

/**
 * Service d'INSCRIPTION libre-service (Brique 3, phase A) — écritures Supabase.
 * Crée : compte (auth) → resto (`tenants`) → menu exemple (catégories + produits).
 * I/O isolée et tolérante : on renvoie un résultat clair, l'UI décide quoi afficher.
 * La logique pure (slug, validation, code invitation) est dans signup.ts (testée à part).
 */

export interface SignupResult {
  ok: boolean;
  /** id du tenant créé (si succès). */
  tenantId?: string;
  /** adresse du site mis en ligne (ex. "mon-resto.lilandco-services.fr"). */
  siteUrl?: string;
  /** chemin du site vivant immédiat, sans DNS (ex. "/r/mon-resto"). */
  sitePath?: string;
  /** message d'erreur prêt à afficher (si échec). */
  error?: string;
}

/** Un article du menu exemple posé à l'inscription. */
interface StarterProduct {
  name: string;
  priceCents: number;
}
/** Une catégorie du menu exemple + ses articles. */
interface StarterCategory {
  name: string;
  emoji: string;
  products: StarterProduct[];
}

/**
 * Menu fast-food exemple posé sur un nouveau resto (phase A).
 * Le resto le modifie ensuite librement via son back-office (Brique 2).
 */
export const STARTER_MENU: StarterCategory[] = [
  {
    name: 'Burgers',
    emoji: '🍔',
    products: [
      { name: 'Cheeseburger', priceCents: 900 },
      { name: 'Double Bacon', priceCents: 1200 },
      { name: 'Burger Veggie', priceCents: 1000 },
    ],
  },
  {
    name: 'Accompagnements',
    emoji: '🍟',
    products: [
      { name: 'Frites maison', priceCents: 400 },
      { name: 'Potatoes', priceCents: 450 },
    ],
  },
  {
    name: 'Boissons',
    emoji: '🥤',
    products: [
      { name: 'Soda 33cl', priceCents: 250 },
      { name: 'Eau minérale', priceCents: 200 },
    ],
  },
];

/**
 * Pose le menu exemple en base pour un tenant donné.
 * Insère les catégories (avec position) puis les produits rattachés.
 * Tolérant : log + on continue (un menu partiel vaut mieux qu'un échec d'inscription).
 */
export async function seedStarterMenu(tenantId: string): Promise<void> {
  for (let ci = 0; ci < STARTER_MENU.length; ci++) {
    const cat = STARTER_MENU[ci];
    const { data: created, error } = await supabase
      .from('categories')
      .insert({
        tenant_id: tenantId,
        name: cat.name,
        slug: slugify(cat.name),
        emoji: cat.emoji,
        position: ci,
        is_visible: true,
      })
      .select('id')
      .single();
    if (error || !created) {
      console.warn('[signup] catégorie exemple échouée:', error?.message);
      continue;
    }
    const categoryId = (created as { id: string }).id;
    const rows = cat.products.map((p, pi) => ({
      tenant_id: tenantId,
      category_id: categoryId,
      name: p.name,
      slug: slugify(p.name),
      base_price_cents: p.priceCents,
      is_available: true,
      is_featured: false,
      position: pi,
    }));
    const { error: prodErr } = await supabase.from('products').insert(rows);
    if (prodErr) console.warn('[signup] produits exemple échoués:', prodErr.message);
  }
}

/** Le slug est-il déjà pris par un autre resto ? (contrôle unicité avant création) */
export async function isSlugTaken(slug: string): Promise<boolean> {
  const { data } = await supabase
    .from('tenants')
    .select('id')
    .eq('slug', slug)
    .maybeSingle();
  return !!data;
}

/**
 * Inscrit un nouveau restaurant de bout en bout (phase A) :
 * 1) vérifie l'unicité du slug, 2) crée le compte (auth), 3) appelle la fonction
 * sécurisée `register_restaurant` (RPC) qui crée — en une transaction côté base —
 * le resto + le lien propriétaire (tenant_users) + le menu exemple.
 *
 * Pourquoi une RPC : la RLS interdit à juste titre l'insert direct dans `tenants`.
 * La fonction SECURITY DEFINER (cf. sql/18_signup_rpc.sql) encadre précisément ce
 * seul scénario sans ouvrir la base. Le formulaire est supposé déjà validé.
 * Le site est « live » dès la création (le socle multi-resto résout par adresse).
 */
export async function registerRestaurant(form: SignupForm): Promise<SignupResult> {
  if (await isSlugTaken(form.slug)) {
    return { ok: false, error: 'Cette adresse est déjà prise. Choisissez-en une autre.' };
  }

  // 1) Compte restaurateur (Supabase Auth). Ouvre la session → auth.uid() côté RPC.
  const { error: authError } = await supabase.auth.signUp({
    email: form.email,
    password: form.password,
  });
  if (authError) {
    return { ok: false, error: `Création du compte impossible : ${authError.message}` };
  }

  // 2) Création sécurisée resto + lien propriétaire + menu exemple (1 appel transactionnel).
  const { data: tenantId, error: rpcError } = await supabase.rpc('register_restaurant', {
    p_slug: form.slug,
    p_name: form.restaurantName,
    p_country: form.country,
    p_currency: currencyForCountry(form.country),
    p_email: form.email,
  });
  if (rpcError || !tenantId) {
    return { ok: false, error: `Création du restaurant impossible : ${rpcError?.message ?? ''}` };
  }

  // 2bis) Formule de base allégée (best-effort) : les modules OPTION (fidélité, avis,
  //   campagnes) sont DÉSACTIVÉS par défaut pour un NOUVEAU resto → l'admin affiche une
  //   vitrine incitant à souscrire. (YumLink Contrôle est OFF par défaut sans flag.)
  //   N'impacte QUE les nouveaux comptes : les clients existants n'ont pas ces flags.
  try {
    await Promise.all([
      setTenantFeature(String(tenantId), 'loyalty', false),
      setTenantFeature(String(tenantId), 'reviews', false),
      setTenantFeature(String(tenantId), 'campaigns', false),
    ]);
  } catch {
    // ignoré (non bloquant).
  }

  // 3) Bibliothèque d'options par défaut (best-effort) : sauces, crudités, viandes,
  //    suppléments, boissons, desserts… prêtes à affecter aux produits. Un échec ne
  //    doit pas faire échouer l'inscription (le resto peut créer ses options à la main).
  try {
    await seedDefaultOptions(String(tenantId));
  } catch {
    // ignoré volontairement (seed non bloquant).
  }

  // 4) Email de bienvenue (best-effort) : liens site + back-office + connexion,
  //    pour que le restaurateur puisse se connecter et finir son menu.
  //    Un échec d'envoi ne doit JAMAIS faire échouer l'inscription.
  try {
    const appOrigin = typeof window !== 'undefined' ? window.location.origin : '';
    await supabase.functions.invoke('send-welcome-email', {
      body: { tenant_id: String(tenantId), to_email: form.email, app_origin: appOrigin },
    });
  } catch {
    // ignoré volontairement (envoi non bloquant).
  }

  return {
    ok: true,
    tenantId: String(tenantId),
    siteUrl: siteUrlFor(form.slug, form.country),
    sitePath: sitePathFor(form.slug),
  };
}
